import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShoppingCart, MailWarning, Send, BadgeCheck } from "lucide-react";
import { api } from "@/lib/api";
import { FindoSpinner } from "@/components/FindoSpinner";

const PERIODS = [7, 30, 90];
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const eur = (n) => `${Number(n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} €`;

const STATUS = {
  initiated: ["En attente (< 24 h)", "bg-slate-100 text-slate-700"],
  abandoned: ["Abandonné", "bg-amber-100 text-amber-800"],
  completed: ["Payé", "bg-emerald-100 text-emerald-800"],
};

function Kpi({ icon: Icon, label, value, sub, tone, testid }) {
  return (
    <div className="rounded-xl border border-border p-4 bg-white" data-testid={testid}>
      <div className={`h-9 w-9 rounded-lg grid place-items-center ${tone}`}><Icon size={18} /></div>
      <p className="mt-3 text-2xl font-extrabold text-findo-navy" data-testid={`${testid}-value`}>{value}</p>
      <p className="text-sm font-semibold text-slate-700">{label}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function Row({ r }) {
  const [label, cls] = r.recovered_at ? ["Payé après relance", "bg-emerald-100 text-emerald-800"] : (STATUS[r.status] || [r.status, "bg-slate-100"]);
  return (
    <tr className="border-t border-border" data-testid={`abandon-row-${r.id}`}>
      <td className="px-3 py-2 whitespace-nowrap text-slate-600">{fmtDate(r.initiated_at)}</td>
      <td className="px-3 py-2"><p className="font-semibold text-slate-800 truncate max-w-[220px]">{r.org_name || "Nouvelle inscription"}</p><p className="text-xs text-slate-500 truncate max-w-[220px]">{r.email}</p></td>
      <td className="px-3 py-2 whitespace-nowrap">{r.plan_name} <span className="text-xs text-slate-500">({r.billing_cycle === "annual" ? "annuel" : "mensuel"})</span></td>
      <td className="px-3 py-2 whitespace-nowrap font-semibold">{eur(r.amount_eur)}</td>
      <td className="px-3 py-2"><span className={`text-xs font-bold px-2 py-0.5 rounded-full ${cls}`}>{label}</span></td>
      <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">{r.relance_envoyee ? `Oui · ${fmtDate(r.relance_sent_at)}` : "Non"}</td>
    </tr>
  );
}

export default function CheckoutAbandonsPanel() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api(`/admin/checkout-abandons/stats?days=${days}`)
      .then(setData).catch((e) => toast.error(e.message)).finally(() => setLoading(false));
  }, [days]);

  return (
    <section className="bg-white rounded-2xl border border-border card-shadow p-5 mb-6" data-testid="checkout-abandons-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display font-bold text-lg text-findo-navy">Abandons de paiement</h3>
          <p className="text-sm text-slate-500">Paiements d'abonnement Stripe commencés mais non finalisés, relance unique à 24 h, paiement dans les 7 jours qui suivent.</p>
        </div>
        <div className="flex items-center gap-1 rounded-full bg-slate-100 p-1" data-testid="abandons-period">
          {PERIODS.map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`abandons-period-${d}`}
              className={`px-3 py-1 rounded-full text-sm font-semibold transition-colors ${days === d ? "bg-findo-navy text-white" : "text-slate-600 hover:text-slate-900"}`}>
              {d} j
            </button>
          ))}
          {loading && <FindoSpinner size={14} className="mx-1" />}
        </div>
      </div>

      {data && (
        <>
          <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi icon={ShoppingCart} label="Paiements commencés" value={data.started} tone="bg-findo-indigo/10 text-findo-indigo" testid="kpi-started"
              sub={`${data.completed_directly} payés sans relance · ${data.pending} en attente`} />
            <Kpi icon={MailWarning} label="Abandons" value={data.abandoned} tone="bg-amber-100 text-amber-700" testid="kpi-abandoned"
              sub="Non payés après 24 h" />
            <Kpi icon={Send} label="Relances envoyées" value={data.reminders_sent} tone="bg-sky-100 text-sky-700" testid="kpi-reminders" sub="1 email maximum par abandon" />
            <Kpi icon={BadgeCheck} label="Payés après relance" value={data.recovered} tone="bg-emerald-100 text-emerald-700" testid="kpi-recovered"
              sub={`Taux de récupération : ${data.recovery_rate} %`} />
          </div>

          <div className="mt-5 overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm" data-testid="abandons-table">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr><th className="px-3 py-2">Date</th><th className="px-3 py-2">Partenaire</th><th className="px-3 py-2">Offre</th><th className="px-3 py-2">Montant</th><th className="px-3 py-2">Statut</th><th className="px-3 py-2">Relance</th></tr>
              </thead>
              <tbody>
                {data.recent.length === 0
                  ? <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400" data-testid="abandons-empty">Aucun paiement commencé sur la période.</td></tr>
                  : data.recent.map((r) => <Row key={r.id} r={r} />)}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
